/**
 * useSurvey Hook
 * 
 * This hook manages all survey-related state and operations for the Surveys module.
 * It handles the Design page (SurveyJS Creator) and Actions page (rules configuration).
 * 
 * KEY FEATURE: ID-Based Element Tracking
 * ----------------------------------------
 * Questions, rows, and columns are assigned unique IDs that persist across renames.
 * This ensures that when elements are renamed in the Design page, the associated
 * rules in the Actions page are correctly updated rather than broken.
 * 
 * ID Types:
 * - questionId: Unique identifier for each question (9-char random string)
 * - rowId: Unique identifier for each matrix row
 * - columnId: Unique identifier for each column/choice option
 * 
 * Rule Tracking Fields:
 * - _questionInternalId / questionId: Links rule to question by ID
 * - matrixSubQuestionRowId: Links rule to specific matrix row by ID
 * - conditionColumnId: Links rule to specific column/choice by ID
 * 
 * Data Flow:
 * 1. On load: Ensure all elements have IDs, update rules with ID references
 * 2. On design change: Track ID->name mappings, update rules with new names
 * 3. On save: IDs are persisted in the survey details JSON
 * 
 * @see handleDesignChange - Main function for Design page changes
 * @see generateId - Creates 9-char random unique IDs
 * @see ensureRowIds / ensureColumnIds - Ensures rows/columns have IDs
 * @see normalizeRows / normalizeColumns - Normalizes and tracks changes
 * @see updateRulesForRowChanges / updateRulesForColumnChanges - ID-based rule updates
 */

import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { actions as campaignActions } from 'store/campaign';
import { actions } from '../../store/surveys';
import { produce } from 'immer';
import FormController from '../../common/formController';
import Joi from 'joi';
import PermissionCheck from 'PermissionsCheck.js';
import validators from './validators';
import {
  sendSurveyResultResponse,
  setSurveySettingsData
} from 'store/surveys/actions';
import {
  getSurveyTemplate,
  setCurrentSurveyTemplate,
  unsetCustomizedTemplate
} from 'store/surveyTemplate/actions';
import { ProductType } from 'constants.js';
import { getSchemas } from 'store/schema/actions';

const { getCampaigns } = campaignActions;
const {
  getSurvey,
  getSurveyResults,
  setCurrentSurvey,
  setCopyCurrentSurvey,
  updateSurvey,
  createSurvey,
  getSurveyLogoUrl,
  gettingPaginatedSurveysSucceeded
} = actions;

let formController = new FormController();
const MATRIX_QUESTION_TYPES = ['matrix', 'matrixdropdown', 'matrixdynamic'];

const hasMatrixRows = question => {
  if (!question?.rows || !MATRIX_QUESTION_TYPES.includes(question?.type)) {
    return false;
  }

  return Array.isArray(question.rows) || typeof question.rows === 'string' || (typeof question.rows === 'object' && question.rows !== null);
};

const getQuestionOptions = question => {
  if (Array.isArray(question?.choices)) {
    return question.choices;
  }
  if (Array.isArray(question?.columns)) {
    return question.columns;
  }
  return [];
};

export default function useSurvey(nextPath, backPath) {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { surveyId, surveyHash } = useParams();
  const currentContext = useSelector(
    store => store.user.selectedBusinessContext
  );
  const campaigns = useSelector(store => store.campaign.list || []);
  const isGettingCampaign = useSelector(store => store.campaign.getting);

  const supportedInternalSurveyResponseTypeOptions = useSelector(
    store => store.events.supportedInternalSurveyResponseTypes
  );
  const survey = useSelector(store => store.survey.current);
  const surveyPreview = useSelector(store => store.survey.previewUrl);
  const results = useSelector(store => store.survey.results);
  const isLoading = useSelector(store => store.survey.gettingPaginated);
  const isSurveyLoading = useSelector(store => store.survey.getting);
  const isTemplateSaved = useSelector(store => store.survey.isTemplateSaved);
  const individualResults = useSelector(
    store => store.survey.individualResults
  );
  const surveySettingsData = useSelector(
    store => store.survey.surveySettingsData
  );

  const template = useSelector(store => store.surveyTemplate.current);
  const customizedTemplate = useSelector(
    store => store.surveyTemplate.customizedTemplate
  );
  const isCustomizedTemplateSet = useSelector(
    store => store.surveyTemplate.isCustomizedTemplateSet
  );

  const currentBusinessContextId = useSelector(
    store => store.user.selectedBusinessContext.businessContextId
  );

  const listManagementSchema = useSelector(
    store => store.schema.listManagementSchema
  );

  const [validationErrors, setValidationErrors] = useState({});
  const [errorValidationSegment, setErrorValidationSegment] = useState(null);
  const [thisSurvey, setThisSurvey] = useState({
    name: '',
    surveyTheme: {
      primaryColor: '#43a047',
      secondaryColor: '#ffffff',
      fontFamily: 'Times New Roman,serif',
      fontSize: 14
    }
  });
  const [thisSurveyPreview, setThisSurveyPreview] = useState('');
  const [thisSurveyResults, setThisSurveyResults] = useState({});
  const [
    thisIndividualSurveyResults,
    setThisIndividualSurveyResults
  ] = useState([]);
  const [surveySettings, setSurveySettings] = useState({});
  
  // Ref to track if initial survey processing has been done (prevents infinite loops)
  const surveyProcessedRef = useRef(false);

  const validate = (saveAttempted = false) => {
    let segment = errorValidationSegment;
    const joiValidators = Joi.object(validators[segment]);
    const validation = joiValidators.validate(
      segment === 'sendResponseData' ? surveySettings : thisSurvey,
      {
        abortEarly: false,
        allowUnknown: true // allow unknown keys that will be ignored
      }
    );
    let errors = formController.getFormErrors(validation, { saveAttempted });
    setValidationErrors(errors);
    return errors;
  };

  const permissions = PermissionCheck();
  const { hasPermission } = permissions;

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!campaigns?.length > 0) {
      dispatch(getCampaigns({ force: true }));
    }

    if (surveyId) {
      dispatch(getSurvey({ surveyId }));
    }

    if (surveyHash && !history.location.pathname.includes('/response')) {
      dispatch(getSurveyResults({ surveyHash }));
    }
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const copy = queryParams.get('copy');
    const businessContextId = queryParams.get('businessContextId');

    if (copy === 'true' && businessContextId && survey.id && survey.name && !survey._isCopy) {
      setSurveySettingsData(undefined)

      let surveyToCopy = Object.assign(
        {},
        survey
      );
      
      if (surveyToCopy && surveyToCopy.id) {
        delete surveyToCopy.id;
        delete surveyToCopy.surveyHash;
        surveyToCopy._isCopy = true;
        surveyToCopy._isCopyTemplate = true;
        surveyToCopy.name = `${surveyToCopy.name}-copy`;
        surveyToCopy.active = true;
        surveyToCopy.schemaIdentifier = surveyToCopy.schemaIdentifier
          ? surveyToCopy.schemaIdentifier
          : surveyToCopy.productCategory
          ? `${surveyToCopy.productCategory}.${surveyToCopy.productName}.${surveyToCopy.eventTypeCategory}`
          : '';
        // Update businessContextId for copied surveys
        surveyToCopy.businessContextId = currentBusinessContextId;
        // Ensure copied surveys are not locked
        surveyToCopy.locked = false;
        // Clear survey theme imageId when copying across properties
        if (currentBusinessContextId !== businessContextId && surveyToCopy.surveyTheme) {
          let modifiedSurveyTheme = { ...surveyToCopy.surveyTheme };
          delete modifiedSurveyTheme.imageId;
          surveyToCopy.surveyTheme = { ...modifiedSurveyTheme };
        }
        // Clear survey actions when copying across properties due to campaign differences
        if (currentBusinessContextId !== businessContextId) {
          surveyToCopy.surveyQuestionResponseRules = [];
        }
        // Note: We don't update the Redux survey state for copied surveys
        // thisSurvey contains the copied survey data
        // Reset the processing ref when copying a survey
        surveyProcessedRef.current = false;
        setThisSurvey(prevSurvey => ({
          ...surveyToCopy,
          hasMovedToNextStep: prevSurvey.hasMovedToNextStep || false
        }));

        // Reset loading states for copied surveys since no API call is made
        dispatch(gettingPaginatedSurveysSucceeded({ data: [] }));

        const newUrl = `/surveys/details`;
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [location.search, survey.id, survey.name, survey._isCopy]);

  function isValidLogoURL(survey, surveyPreview, surveyId) {
    return (
      survey.surveyTheme?.imageId &&
      !surveyPreview &&
      (surveyId || survey?._isCopy)
    );
  }
  useEffect(() => {
    // Don't sync with Redux survey if we're in copy mode
    const queryParams = new URLSearchParams(location.search);
    const copy = queryParams.get('copy');
    if (copy === 'true' && !thisSurvey?.id) {
      return;
    }

    // Keep copied survey edits until it is actually saved
    const isUnsavedCopyFlow = !!thisSurvey?._isCopy && !survey?.id;
    if (isUnsavedCopyFlow) {
      return;
    }

    if (surveyId || survey.id) {
      if (Object.keys(survey).length > 0) {
        formController = new FormController();
        // Reset the processing ref when loading a new survey
        surveyProcessedRef.current = false;
        setThisSurvey({
          ...survey,
          singleResponse: survey.singleResponse || false });
      }
    } else {
      // Reset the processing ref when loading a new survey
      surveyProcessedRef.current = false;
      setThisSurvey({
        ...survey,
        hasMovedToNextStep: survey.hasMovedToNextStep || false,
        singleResponse: survey.singleResponse || false
      });
    }

    if (surveyHash) {
      if (Object.keys(results).length > 0) {
        formController = new FormController();
        setThisSurveyResults({ ...results });
      }
    }
    if (isValidLogoURL(survey, surveyPreview, surveyId)) {
      dispatch(getSurveyLogoUrl({ survey }));
    }
    if (survey.preview) {
      setThisSurveyPreview(survey.preview);
    } else if (surveyPreview && (surveyId || survey?._isCopy)) {
      setThisSurveyPreview(surveyPreview);
    }
  }, [survey, surveyPreview]);

  useEffect(() => {
    // Only update if preview changed and isn't already set in thisSurvey
    if (thisSurveyPreview && thisSurvey.preview !== thisSurveyPreview) {
      setThisSurvey({ ...thisSurvey, preview: thisSurveyPreview });
    }
  }, [thisSurveyPreview]);

  /**
   * INITIAL LOAD: Process existing survey data
   * 
   * This useEffect runs when a survey is loaded and processes the questions,
   * rules, and promoter scores to ensure all elements have unique IDs.
   * 
   * ID Assignment on Load:
   * - Questions get a questionId (generated if missing)
   * - Rows get a rowId via ensureRowIds()
   * - Columns/Choices get a columnId via ensureColumnIds()
   * 
   * Rules are updated to store ID references:
   * - _questionInternalId / questionId - links rule to question
   * - matrixSubQuestionRowId - links rule to specific matrix row
   * - conditionColumnId - links rule to specific column/choice option
   * 
   * This ensures that when the survey is edited later, we can track elements
   * by ID even if their display names/values are changed.
   */
  useEffect(() => {
    // IMPORTANT: Only process on initial load when details exist but questions haven't been extracted yet
    // This prevents infinite loops when thisSurvey is updated elsewhere (e.g., handleDesignChange)
    // Using ref to track processing state as it persists across renders without causing re-renders
    if (thisSurvey?.details && !thisSurvey.questions && !surveyProcessedRef.current) {
      // Mark as processed immediately to prevent re-entry
      surveyProcessedRef.current = true;
      
      let detailsObj = JSON.parse(thisSurvey?.details);
      let details = detailsObj?.pages;
      let tempQuestions = [];
      let tempQuestionRules = [];
      let tempPromoterScores = [];

      // Ensure all questions have unique IDs, and all rows/columns have IDs too
      details?.forEach(page => {
        if (page.elements) {
          page.elements = page.elements.map(element => {
            const hadId = !!element.questionId;
            if (!element.questionId) {
              element.questionId = generateId();
            }
            // Ensure rows have IDs
            if (element.rows) {
              element.rows = ensureRowIds(element.rows);
            }
            // Ensure columns have IDs
            if (element.columns) {
              element.columns = ensureColumnIds(element.columns);
            }
            // Ensure choices have IDs (treated same as columns)
            if (element.choices) {
              element.choices = ensureColumnIds(element.choices);
            }
            return element;
          });
          tempQuestions = [...tempQuestions, ...page.elements];
        }
      });

      // Update details string with new IDs
      const updatedDetails = JSON.stringify(detailsObj);

      tempQuestions = tempQuestions.map(q => ({
        ...q,
        _internalId: q.questionId // Sync internal ID with persistent ID
      }));

      if (thisSurvey?.surveyQuestionResponseRules?.length > 0) {
        tempQuestionRules = [...thisSurvey.surveyQuestionResponseRules];
        tempQuestionRules = tempQuestionRules.filter(r => {
          const lookupId = r._questionInternalId || r.questionId;
          const foundQ = lookupId 
            ? tempQuestions.find(q => q._internalId === lookupId || q.questionId === lookupId)
            : tempQuestions.find(q => q.name === r.surveyQuestionName);
          return (
            r.surveyQuestionName === '' ||
            tempQuestions.find(q => r.surveyQuestionName === q.name)
          );
        });
        tempQuestionRules = tempQuestionRules.map(r => {
          const lookupId = r._questionInternalId || r.questionId;
          let q = lookupId 
            ? tempQuestions.find(q => q._internalId === lookupId || q.questionId === lookupId)
            : tempQuestions.find(q => q.name === r.surveyQuestionName);
          
          // Find rowId for matrixSubQuestionRow if present
          let matrixSubQuestionRowId = r.matrixSubQuestionRowId;
          if (!matrixSubQuestionRowId && r.matrixSubQuestionRow && q?.rows) {
            const matchedRow = q.rows.find(row => {
              const rowValue = typeof row === 'string' ? row : (row.value || row.text);
              return rowValue === r.matrixSubQuestionRow;
            });
            matrixSubQuestionRowId = matchedRow?.rowId;
          }
          
          // Find columnId for condition if present (condition holds a column/choice value)
          let conditionColumnId = r.conditionColumnId;
          if (!conditionColumnId && r.condition) {
            const columnsOrChoices = q?.choices || q?.columns || [];
            const matchedCol = columnsOrChoices.find(col => {
              const colValue = typeof col === 'string' ? col : (col.value || col.text);
              return colValue === r.condition;
            });
            conditionColumnId = matchedCol?.columnId;
          }
          
          r = {
            ...r,
            _questionInternalId: q?._internalId,
            questionId: q?.questionId,
            type: q?.type,
            surveyQuestionName: q?.name || r.surveyQuestionName,
            matrixSubQuestionRowId: matrixSubQuestionRowId,
            conditionColumnId: conditionColumnId,
            choices: q?.choices || q?.columns || [],
            subQuestionOptions: q?.rows || [] 
          };
          return r;
        });
      }

      if (thisSurvey?.postProcessing?.promoterScores?.length > 0) {
        // NOTE: score.questions must remain a STRING ARRAY (question names) for ScoreBuilder.js compatibility
        tempPromoterScores = [...thisSurvey.postProcessing.promoterScores];
        tempPromoterScores = tempPromoterScores.map(score => {
          // Filter questions to only include those that still exist in tempQuestions
          const validQuestionNames = (score.questions || []).filter(qName => {
            // score.questions is already a string array of question names
            const exists = tempQuestions.some(tq => tq.name === qName);
            return exists;
          });
          return {
            ...score,
            questions: validQuestionNames
          };
        });
      }

      setThisSurvey({
        ...thisSurvey,
        details: updatedDetails,
        questions: tempQuestions,
        surveyQuestionResponseRules: tempQuestionRules,
        postProcessing: {
          ...thisSurvey.postProcessing,
          promoterScores: tempPromoterScores.length > 0 ? tempPromoterScores : thisSurvey.postProcessing?.promoterScores
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thisSurvey?.details, thisSurvey?.questions]);

  useEffect(() => {
    if (surveyHash && Object.keys(results).length > 0) {
      formController = new FormController();
      setThisSurveyResults({ ...results });
    }
  }, [results]);

  useEffect(() => {
    setSurveySettings(surveySettingsData);
  }, [surveySettingsData]);

  useEffect(() => {
    if (surveyHash) {
      formController = new FormController();
      setThisIndividualSurveyResults(individualResults);
    }
  }, [individualResults]);

  useEffect(() => {
    if (survey.schemaIdentifier) {
      setCurrentTemplateDetails();
    }
  }, [survey.schemaIdentifier]);

  useEffect(() => {
    if (isCustomizedTemplateSet) {
      onClickSendResponse(true);
    }
  }, [isCustomizedTemplateSet]);

  useEffect(() => {
    if (thisSurvey.productType && thisSurvey.productType === ProductType.List && listManagementSchema) {
      const newSchemaIdentifier = 
        listManagementSchema?.eventProductCategory +
        '.' +
        listManagementSchema?.eventProductName +
        '.' +
        listManagementSchema?.eventTypeCategory || '';
      
      // Only update if schema identifier actually changed
      if (thisSurvey.schemaIdentifier !== newSchemaIdentifier) {
        setThisSurvey({
          ...thisSurvey,
          productCategory: listManagementSchema?.eventProductCategory || '',
          productName: listManagementSchema?.eventProductName || '',
          eventTypeCategory: listManagementSchema?.eventTypeCategory || '',
          schemaIdentifier: newSchemaIdentifier
        });
      }
    }
  }, [listManagementSchema]);

  // ============================================================================
  // ID-BASED TRACKING SYSTEM
  // ============================================================================
  // This system assigns unique IDs to questions, rows, and columns to enable
  // reliable tracking across renames and reordering. When a user renames a
  // question/row/column in the Design page, the corresponding rules in the
  // Actions page are automatically updated because we track by ID, not by value.
  //
  // Key IDs:
  // - questionId: Unique ID for each question (persisted in details JSON)
  // - rowId: Unique ID for each matrix row (enables rename tracking)
  // - columnId: Unique ID for each column/choice (enables rename tracking)
  //
  // Rule fields for ID-based lookup:
  // - matrixSubQuestionRowId: Links rule to a specific row by ID
  // - conditionColumnId: Links rule to a specific column/choice by ID
  // ============================================================================

  /**
   * Generates a unique 9-character alphanumeric ID
   * @returns {string} Random ID string
   */
  const generateId = () => Math.random().toString(36).substr(2, 9);

  /**
   * Ensures all rows in a matrix question have unique rowId values.
   * Converts string rows to objects with value, text, and rowId properties.
   * Preserves existing rowIds to maintain rule associations.
   * 
   * @param {Array} rows - Array of row items (strings or objects)
   * @returns {Array} Array of row objects with guaranteed rowId
   */
  const ensureRowIds = (rows) => {
    if (!rows) return rows;
    return rows.map(row => {
      if (typeof row === 'object' && row !== null) {
        return {
          ...row,
          rowId: row.rowId || generateId()
        };
      }
      // Convert string rows to objects with IDs
      return {
        value: row,
        text: row,
        rowId: generateId()
      };
    });
  };

  /**
   * Ensures all columns/choices have unique columnId values.
   * Converts string columns to objects with value, text, and columnId properties.
   * Preserves existing columnIds to maintain rule associations.
   * 
   * @param {Array} columns - Array of column/choice items (strings or objects)
   * @returns {Array} Array of column objects with guaranteed columnId
   */
  const ensureColumnIds = (columns) => {
    if (!columns) return columns;
    return columns.map(column => {
      if (typeof column === 'object' && column !== null) {
        return {
          ...column,
          columnId: column.columnId || generateId()
        };
      }
      // Convert string columns to objects with IDs
      return {
        value: column,
        text: column,
        columnId: generateId()
      };
    });
  };

  /**
   * Normalizes columns/choices by ensuring value equals text (SurveyJS requirement).
   * Tracks value changes by columnId for rule synchronization.
   * This is called when navigating to the Actions page.
   * 
   * @param {Array} columns - Array of column/choice items
   * @param {string} questionName - Name of the question (for tracking)
   * @param {Map} columnValueChanges - Map to store value changes by question and columnId
   * @returns {Array} Normalized column array
   */
  const normalizeColumns = (columns, questionName, columnValueChanges) => {
    if (!columns) return columns;
    const safeColumns = Array.isArray(columns) ? columns : [columns];
    
    return safeColumns.map(column => {
      if (typeof column === 'object' && column !== null && column.text && column.text.trim() !== '') {
        const columnId = column.columnId || generateId();
        // Track value change by ID for rule synchronization
        if (column.value !== column.text) {
          if (!columnValueChanges.has(questionName)) {
            columnValueChanges.set(questionName, new Map());
          }
          // Store mapping: columnId -> { oldValue, newValue }
          columnValueChanges.get(questionName).set(columnId, {
            oldValue: column.value,
            newValue: column.text
          });
        }
        return {
          value: column.text,
          text: column.text,
          columnId: columnId
        };
      }
      // Handle string columns - convert to object
      if (typeof column === 'string') {
        return {
          value: column,
          text: column,
          columnId: generateId()
        };
      }
      return column;
    });
  };

  /**
   * Normalizes rows by ensuring value equals text (SurveyJS requirement).
   * Tracks value changes by rowId for rule synchronization.
   * This is called when navigating to the Actions page.
   * 
   * @param {Array} rows - Array of row items
   * @param {string} questionName - Name of the question (for tracking)
   * @param {Map} rowValueChanges - Map to store value changes by question and rowId
   * @returns {Array} Normalized row array
   */
  const normalizeRows = (rows, questionName, rowValueChanges) => {
    if (!rows) return rows;
    if (!Array.isArray(rows) && typeof rows !== 'string' && (typeof rows !== 'object' || rows === null)) {
      return rows;
    }
    const safeRows = Array.isArray(rows) ? rows : [rows];
    
    return safeRows.map(row => {
      if (typeof row === 'object' && row !== null && row.text && row.text.trim() !== '') {
        const rowId = row.rowId || generateId();
        // Track value change by ID for rule synchronization
        if (row.value !== row.text) {
          if (!rowValueChanges.has(questionName)) {
            rowValueChanges.set(questionName, new Map());
          }
          // Store mapping: rowId -> { oldValue, newValue }
          rowValueChanges.get(questionName).set(rowId, {
            oldValue: row.value,
            newValue: row.text
          });
        }
        return {
          value: row.text,
          text: row.text,
          rowId: rowId
        };
      }
      // Handle string rows - convert to object
      if (typeof row === 'string') {
        return {
          value: row,
          text: row,
          rowId: generateId()
        };
      }
      return row;
    });
  };

  /**
   * Normalizes rate values by converting text to numbers where possible.
   * Also calculates the maximum rating value for rateMax property.
   * 
   * @param {Array} rateValues - Array of rate value items
   * @returns {Object} Object with normalized array and maxValue
   */
  const normalizeRateValues = (rateValues) => {
    if (!rateValues) return { normalized: rateValues, maxValue: null };
    const safeRateValues = Array.isArray(rateValues) ? rateValues : [rateValues];
    
    const normalized = safeRateValues.map(rateValue => {
      if (typeof rateValue === 'object' && rateValue !== null) {
        return {
          value: !isNaN(Number(rateValue.text)) ? Number(rateValue.text) : rateValue.text,
          text: rateValue.text
        };
      }
      return {
        value: rateValue,
        text: rateValue
      };
    });
    
    // Calculate max value from numeric rate values
    const numericRateValues = normalized
      .map(rv => Number(rv.text))
      .filter(value => !isNaN(value));
    const maxValue = numericRateValues.length > 0 ? Math.max(...numericRateValues) : null;
    
    return { normalized, maxValue };
  };

  /**
   * Normalizes a single question element by processing its rows, columns,
   * choices, and rate values. Syncs element name with title.
   * 
   * @param {Object} element - Question element to normalize
   * @param {Map} rowValueChanges - Map to track row value changes
   * @param {Map} columnValueChanges - Map to track column value changes
   * @returns {Object} Normalized element
   */
  const normalizeElement = (element, rowValueChanges, columnValueChanges) => {
    // Sync element name with title if title exists
    let modifiedElement = { ...element, ...(element.title && { name: element.title }) };
    const questionName = modifiedElement.name || element.name;
    
    // Normalize columns (for matrix questions)
    if (element.columns) {
      modifiedElement.columns = normalizeColumns(element.columns, questionName, columnValueChanges);
    }
    
    // Normalize choices (for radio, checkbox, dropdown)
    if (element.choices) {
      modifiedElement.choices = normalizeColumns(element.choices, questionName, columnValueChanges);
    }
    
    // Normalize rows (for matrix questions)
    if (hasMatrixRows(element)) {
      modifiedElement.rows = normalizeRows(element.rows, questionName, rowValueChanges);
    }
    
    // Normalize rate values (for rating questions)
    if (element.rateValues) {
      const { normalized, maxValue } = normalizeRateValues(element.rateValues);
      modifiedElement.rateValues = normalized;
      if (maxValue !== null) {
        modifiedElement.rateMax = maxValue;
      }
    }
    
    return modifiedElement;
  };

  /**
   * Updates rules when row values change during normalization.
   * Uses rowId-based lookup to find and update the correct rule,
   * even if multiple rows have the same display value.
   * 
   * @param {Array} rules - Array of survey question response rules
   * @param {Map} rowValueChanges - Map<questionName, Map<rowId, {oldValue, newValue}>>
   * @returns {Array} Updated rules array
   */
  const updateRulesForRowChanges = (rules, rowValueChanges) => {
    if (!rules) return rules;
    
    return rules.map(rule => {
      let updatedRule = { ...rule };
      
      // Update matrixSubQuestionName if the row it references changed (by rowId)
      if (rule.matrixSubQuestionRowId && rowValueChanges.has(rule.surveyQuestionName)) {
        const questionRowChanges = rowValueChanges.get(rule.surveyQuestionName);
        // questionRowChanges is Map<rowId, { oldValue, newValue }>
        if (questionRowChanges.has(rule.matrixSubQuestionRowId)) {
          const change = questionRowChanges.get(rule.matrixSubQuestionRowId);
          updatedRule.matrixSubQuestionName = change.newValue;
        }
      }
      
      // Update subQuestionOptions array with new row values (by rowId)
      if (rule.subQuestionOptions && rowValueChanges.has(rule.surveyQuestionName)) {
        const questionRowChanges = rowValueChanges.get(rule.surveyQuestionName);
        updatedRule.subQuestionOptions = rule.subQuestionOptions.map(option => {
          if (typeof option === 'object' && option !== null && option.rowId && questionRowChanges.has(option.rowId)) {
            const change = questionRowChanges.get(option.rowId);
            return {
              ...option,
              value: change.newValue,
              text: change.newValue
            };
          }
          return option;
        });
      }
      
      return updatedRule;
    });
  };

  /**
   * Updates rules when column/choice values change during normalization.
   * Uses columnId-based lookup to find and update the correct rule,
   * even if multiple columns have the same display value.
   * 
   * @param {Array} rules - Array of survey question response rules
   * @param {Map} columnValueChanges - Map<questionName, Map<columnId, {oldValue, newValue}>>
   * @returns {Array} Updated rules array
   */
  const updateRulesForColumnChanges = (rules, columnValueChanges) => {
    if (!rules) return rules;
    
    return rules.map(rule => {
      let updatedRule = { ...rule };
      
      // Update conditionData if the column it references changed (by columnId)
      if (rule.conditionColumnId && rule.conditionData && columnValueChanges.has(rule.surveyQuestionName)) {
        const questionColumnChanges = columnValueChanges.get(rule.surveyQuestionName);
        // questionColumnChanges is Map<columnId, { oldValue, newValue }>
        if (questionColumnChanges.has(rule.conditionColumnId)) {
          const change = questionColumnChanges.get(rule.conditionColumnId);
          updatedRule.conditionData = {
            ...rule.conditionData,
            conditionValue: change.newValue,
            conditionValueText: change.newValue
          };
        }
      }
      
      // Update choices array with new column values (by columnId)
      if (rule.choices && columnValueChanges.has(rule.surveyQuestionName)) {
        const questionColumnChanges = columnValueChanges.get(rule.surveyQuestionName);
        updatedRule.choices = rule.choices.map(choice => {
          if (typeof choice === 'object' && choice !== null && choice.columnId && questionColumnChanges.has(choice.columnId)) {
            const change = questionColumnChanges.get(choice.columnId);
            return {
              ...choice,
              value: change.newValue,
              text: change.newValue
            };
          }
          return choice;
        });
      }
      
      return updatedRule;
    });
  };

  /**
   * Updates the questions array with normalized row/column values.
   * Uses ID-based lookup to apply changes to the correct items.
   * 
   * @param {Array} questions - Array of question objects
   * @param {Map} rowValueChanges - Map of row value changes by question and rowId
   * @param {Map} columnValueChanges - Map of column value changes by question and columnId
   * @returns {Array} Updated questions array
   */
  const updateQuestionsArray = (questions, rowValueChanges, columnValueChanges) => {
    if (!questions) return questions;
    
    return questions.map(question => {
      let updatedQuestion = { ...question };
      
      // Update rows if values changed (by rowId)
      if (question.rows && rowValueChanges.has(question.name)) {
        const questionRowChanges = rowValueChanges.get(question.name);
        updatedQuestion.rows = question.rows.map(row => {
          if (typeof row === 'object' && row !== null && row.rowId && questionRowChanges.has(row.rowId)) {
            const change = questionRowChanges.get(row.rowId);
            return {
              ...row,
              value: change.newValue,
              text: change.newValue
            };
          }
          return row;
        });
      }
      
      // Update columns if values changed (by columnId)
      if (question.columns && columnValueChanges.has(question.name)) {
        const questionColumnChanges = columnValueChanges.get(question.name);
        updatedQuestion.columns = question.columns.map(column => {
          if (typeof column === 'object' && column !== null && column.columnId && questionColumnChanges.has(column.columnId)) {
            const change = questionColumnChanges.get(column.columnId);
            return {
              ...column,
              value: change.newValue,
              text: change.newValue
            };
          }
          return column;
        });
      }
      
      // Update choices if values changed (by columnId)
      if (question.choices && columnValueChanges.has(question.name)) {
        const questionColumnChanges = columnValueChanges.get(question.name);
        updatedQuestion.choices = question.choices.map(choice => {
          if (typeof choice === 'object' && choice !== null && choice.columnId && questionColumnChanges.has(choice.columnId)) {
            const change = questionColumnChanges.get(choice.columnId);
            return {
              ...choice,
              value: change.newValue,
              text: change.newValue
            };
          }
          return choice;
        });
      }
      
      return updatedQuestion;
    });
  };

  const goNext = event => {
    const errors = validate(true);
    if (!errors.meta.clean) return;

    let modifiedSurvey = thisSurvey;
    
    // Only normalize data when navigating to the actions page
    if (event.currentTarget.attributes.link.value === "/surveys/actions") {
      try {
        // Parse survey details
        const surveyDetails = JSON.parse(thisSurvey.details);
        if (!surveyDetails?.pages) {
          console.warn('Survey details missing pages structure');
          dispatch(setCurrentSurvey({ ...modifiedSurvey, hasMovedToNextStep: true }));
          formController = new FormController();
          history.push(`${nextPath || event.currentTarget.attributes.link.value}${thisSurvey.id && !thisSurvey._isCopy ? `/${thisSurvey.id}` : ''}`);
          return;
        }
        
        // Track value changes for synchronizing rules and questions
        const rowValueChanges = new Map();
        const columnValueChanges = new Map();
        
        // Normalize all elements in all pages
        const normalizedPages = surveyDetails.pages.map(page => ({
          ...page,
          elements: (page.elements || []).map(element => 
            normalizeElement(element, rowValueChanges, columnValueChanges)
          )
        }));
        
        // Update rules to reflect normalized row values
        let updatedRules = updateRulesForRowChanges(
          thisSurvey.surveyQuestionResponseRules,
          rowValueChanges
        );
        
        // Update rules to reflect normalized column/choice values
        updatedRules = updateRulesForColumnChanges(updatedRules, columnValueChanges);
        
        // Update questions array to reflect normalized values
        const updatedQuestions = updateQuestionsArray(
          thisSurvey.questions,
          rowValueChanges,
          columnValueChanges
        );
        
        // Build modified survey with all normalized data
        modifiedSurvey = {
          ...thisSurvey,
          details: JSON.stringify({ ...surveyDetails, pages: normalizedPages }),
          ...(updatedRules && { surveyQuestionResponseRules: updatedRules }),
          ...(updatedQuestions && { questions: updatedQuestions })
        };
      } catch (error) {
        console.error('Error normalizing survey data:', error);
        // Continue with original survey data if normalization fails
      }
    }
    
    // Dispatch and navigate
    dispatch(setCurrentSurvey({ ...modifiedSurvey, hasMovedToNextStep: true }));
    formController = new FormController();
    history.push(
      `${nextPath || event.currentTarget.attributes.link.value}${
        thisSurvey.id && !thisSurvey._isCopy ? `/${thisSurvey.id}` : ''
      }`
    );
  };

  const goBack = event => {
    dispatch(setCurrentSurvey({ ...thisSurvey }));
    history.push(
      `${backPath || event.currentTarget.attributes.link.value}${
        thisSurvey.id && !thisSurvey._isCopy ? `/${thisSurvey.id}` : ''
      }`
    );
  };

  const navBackOnError = path => {
    if (
      thisSurvey.hasOwnProperty('hasMovedToNextStep') &&
      !thisSurvey.hasMovedToNextStep
    ) {
      history.push(path);
    }
  };

  const onTabChange = () => {
    dispatch(setCurrentSurvey(thisSurvey));
  };

  // setByPath({}, 'key.key', 123);
  const setByPath = (obj, path, value) => {
    if (typeof path === 'string') {
      path = path.split('.');
    }
    if (obj && path.length === 1) {
      obj[path] = value;
      return;
    }
    return setByPath(obj[path[0]], path.slice(1), value);
  };

  // setImagePreview
  const setPreview = updatedSurvey => {
    if (updatedSurvey.file) {
      let reader = new FileReader();
      let file = updatedSurvey.file;

      reader.onload = e => {
        setThisSurveyPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAutomationChange = event => {
    const { name, value } = event.target;
    const isCopyFlow = !!(thisSurvey._isCopy || thisSurvey._isCopyTemplate);
    if (isCopyFlow && name === 'schemaIdentifier') {
      return;
    }
    let typeOption = {};
    let surveyUpdate = produce(thisSurvey, draft => {
      typeOption = supportedInternalSurveyResponseTypeOptions.find(
        option => option.id === value
      );
      draft.schemaIdentifier = typeOption.id;
      draft.productCategory = typeOption.productCategory;
      draft.eventTypeCategory = typeOption.eventTypeCategory;
      draft.productName = typeOption.productName;
      setByPath(draft, name, value);
    });
    setThisSurvey(surveyUpdate);
    validate();
  };

  const handleLogoChange = event => {
    let updatedSurvey = produce(thisSurvey, surveyDraft => {
      if (event.target.name === 'file') {
        let file = event.target.files[0];
        setByPath(surveyDraft, event.target.name, file);
        setByPath(surveyDraft, 'surveyTheme.fileSize', file?.size);
        setByPath(surveyDraft, 'surveyTheme.fileFormat', file?.type);
      }
    });
    setThisSurvey(updatedSurvey);
    setPreview(updatedSurvey);
    validate();
  };

  const handleChange = events => {
    events = Array.isArray(events) ? events : [events];

    let updatedSurvey = produce(thisSurvey, surveyDraft => {
      if (events.length > 0) {
        events.forEach(event => {
          setByPath(surveyDraft, event.target.name, event.target.value);
        });
      }
    });

    setThisSurvey(updatedSurvey);
    validate();
  };

  const updateEditorTheme = theme => {
    dispatch(setCurrentSurvey({
      ...thisSurvey,
      editorTheme: theme
    }));
  };

  const saveSurvey = () => {
    const errors = validate(true);
    if (errors.meta.clean) {
      dispatch(setCurrentSurvey({ ...thisSurvey }));
      if (thisSurvey.id) {
        dispatch(updateSurvey());
      } else {
        dispatch(createSurvey());
      }
      formController = new FormController();
    }
  };

  const closeSurvey = () => {
    formController = new FormController();
    history.push('/surveys');
  };

  const onBlur = e => {
    formController.onBlur(e);
    validate();
  };

  const onFocus = e => {
    formController.onFocus(e);
    validate();
  };

  const isTemplateReadOnly = () => {
    return (
      (thisSurvey.id &&
        currentBusinessContextId !== thisSurvey.businessContextId) ||
      !hasPermission('Rguest_writeSurvey')
    );
  };

  const shouldShowError = name => {
    return formController.shouldShowError(validationErrors, name);
  };

  const updateCurrentErrorValidationSegment = name => {
    setErrorValidationSegment(name);
  };

  const getErrorMessage = name => {
    return formController.getErrorMessage(validationErrors, name);
  };

  const showFormSaveError = () => {
    return formController.showFormSaveError(validationErrors);
  };

  const handleSurveyPermissions = checkPermission => {
    return hasPermission(checkPermission);
  };

  const setCurrentTemplateDetails = () => {
    let currentTemplate = {
      eventMetaData: [],
      hasMovedToNextStep: false
    };
    currentTemplate.productCategory = survey.productCategory;
    currentTemplate.productName = survey.productName;
    currentTemplate.eventTypeCategory = survey.eventTypeCategory;
    currentTemplate.schemaIdentifier = survey.schemaIdentifier;
    currentTemplate.productType = survey.productType;
    if (survey.productType === ProductType.List) {
      currentTemplate.subscriberListId = survey.subscriberListId;
      currentTemplate.segmentedListId = survey.segmentedListId;
    }
    dispatch(
      setCurrentSurveyTemplate({
        ...currentTemplate,
        schemaId: template.schemaId
      })
    );
  };

  const handleSettingsChange = event => {
    let updatedSurvey = produce(surveySettings, surveyDraft => {
      setByPath(surveyDraft, event.target.name, event.target.value);
    });

    setSurveySettings(updatedSurvey);
    dispatch(setSurveySettingsData(updatedSurvey));
    validate();
  };

  const updateTemplateModelProperty = template => {
    dispatch(
      setSurveySettingsData({
        ...surveySettings,
        surveyTemplateId: template.id,
        surveyTemplateUuid: template.uuid,
        emailSubject: template.emailSubject,
        content: template.content
      })
    );
    validate();
  };

  const closeSurveySettings = () => {
    setSurveySettings({});
    formController = new FormController();
    validate();
  };

  const setSurveyResultData = result => {
    dispatch(
      setSurveySettingsData({
        ...surveySettings,
        ...(result.eventId && 
          {
            eventId: result.eventId, 
            partialCollectionName: result.partialCollectionName
          }),
        ...(result.profileUuid && 
          {
            profileUuid: result.profileUuid,
            listId: result.subscriberListId,
          }),
        id: result.id
      })
    );
  };

  const onClickSendResponse = isCustomized => {
    let errors = validate(true);
    if (
      errors.meta.clean &&
      (!isCustomized || customizedTemplate.emailSubject?.length > 0)
    ) {
      dispatch(
        sendSurveyResultResponse({
          ...(surveySettingsData.eventId && 
            {
              eventId: surveySettingsData.eventId,
              partialCollectionName: surveySettingsData.partialCollectionName
            }),
          ...(surveySettingsData.profileUuid && 
            {
              profileUuid: surveySettingsData.profileUuid,
              listId: surveySettingsData.listId
            }),
          surveyId: surveySettingsData.surveyTemplateId,
          surveyUuid: surveySettingsData.surveyTemplateUuid,
          surveyResultId: surveySettingsData.id,
          content: isCustomized
            ? customizedTemplate.content
            : surveySettingsData.content,
          emailSubject: isCustomized
            ? customizedTemplate.emailSubject
            : surveySettingsData.emailSubject,
          emailDeliveryData: {
            fromName: surveySettingsData.fromName,
            fromAddress: surveySettingsData.fromAddress,
            replyAddress: surveySettingsData.replyAddress,
            bccAddresses: surveySettingsData.bccAddresses,
            ccAddresses: surveySettingsData.ccAddresses
          }
        })
      );
    } else if (isCustomized) {
      dispatch(unsetCustomizedTemplate());
    }
  };

  const onClickCustomizeAndSend = () => {
    let errors = validate(true);
    if (errors.meta.clean) {
      let templateUuid = surveySettings.surveyTemplateUuid;
      dispatch(getSurveyTemplate({ templateUuid }));
      return true;
    } else {
      return false;
    }
  };

  const handleTypeChange = event => {
    if (event.target.value === ProductType.List) {
      dispatch(getSchemas({ showActive: true }));
    }
    setThisSurvey({
      ...thisSurvey,
      productType: event.target.value,
      eventType: '',
      productCategory: '',
      productName: '',
      eventTypeCategory: '',
      schemaIdentifier: '',
      subscriberListId: '',
      segmentedListId: ''
    });
  };

  const handleSubscriberListChange = (event, option) => {
    if (!option) {
      setThisSurvey({
        ...thisSurvey,
        subscriberListId: '',
        segmentedListId: ''
      });
      return;
    }
    setThisSurvey({
      ...thisSurvey,
      ...(option.listId
        ? { subscriberListId: option.listId, segmentedListId: option.id }
        : { subscriberListId: option.id, segmentedListId: '' })
    });
  };

  /**
   * Handles changes from the SurveyJS Design editor.
   * This function is called whenever the survey design is modified and is responsible for:
   * 
   * 1. QUESTION ID MANAGEMENT:
   *    - Assigns unique questionIds to new questions
   *    - Preserves existing questionIds across renames (tracks by ID, not name)
   *    - Prevents duplicate IDs when copy/pasting questions
   * 
   * 2. ROW/COLUMN ID MANAGEMENT:
   *    - Ensures all matrix rows have unique rowIds
   *    - Ensures all columns/choices have unique columnIds
   *    - Preserves IDs across renames for rule synchronization
   * 
   * 3. RULE SYNCHRONIZATION:
   *    - Updates surveyQuestionResponseRules when questions/rows/columns change
   *    - Uses ID-based lookup to handle renames correctly
   *    - Clears rule data when referenced items are deleted
   *    - Only validates column existence for question types that have columns
   *      (dropdown, radiogroup, matrix, etc.), not for text/rating questions
   * 
   * 4. PROMOTER SCORES SYNCHRONIZATION:
   *    - Updates question names in promoterScores when questions are renamed
   *    - Uses questionId to track the question across renames
   * 
   * @param {Object} tempSurvey - The survey object with updated details from the editor
   */
  const handleDesignChange = (tempSurvey) => {
    try {
      let updatedSurvey = { ...tempSurvey };
      const surveyDetails = JSON.parse(updatedSurvey?.details);
      
      if (!surveyDetails?.pages) {
        console.warn('Survey details missing pages structure in handleDesignChange');
        setThisSurvey(updatedSurvey);
        return;
      }
      
      // Extract all question elements from all pages
      // Note: Do NOT sync name with title here - that breaks ID tracking.
      // Name-title sync happens in normalizeElement() when moving to next step.
      let tempQuestions = [];
      surveyDetails.pages.forEach(page => {
        const elements = page?.elements || [];
        if (elements.length > 0) {
          tempQuestions = [...tempQuestions, ...elements];
        }
      });
    
    // -------------------------------------------------------------------------
    // QUESTION IDENTITY TRACKING
    // -------------------------------------------------------------------------
    // Maps to track question identity across changes (renames, reordering, etc.)
    // questionIdentityMap: Stores ID by name and by index for lookup
    // questionIdMap: Maps old name -> questionId (for promoterScores tracking)
    // existingQuestionIds: Set of all known IDs to detect duplicates
    const questionIdentityMap = new Map();
    const questionIdMap = {};
    const existingQuestionIds = new Set();
    
    if (updatedSurvey.questions) {
      updatedSurvey.questions.forEach((oldQ, index) => {
        const id = oldQ.questionId || oldQ._internalId;
        // Store by name for direct lookup
        questionIdentityMap.set(`name:${oldQ.name}`, id);
        // Store in questionIdMap for promoterScores rename tracking
        if (id) {
          questionIdMap[oldQ.name] = id;
          existingQuestionIds.add(id);
        }
        // Store by index for rename detection (same index, different name)
        questionIdentityMap.set(`index:${index}`, { name: oldQ.name, id: id });
      });
    }
    
    // Track IDs assigned during this pass to prevent duplicates (e.g., copy/paste)
    const assignedIdsThisPass = new Set();
    
    // -------------------------------------------------------------------------
    // QUESTION ID ASSIGNMENT
    // -------------------------------------------------------------------------
    // Assign or preserve questionIds using a multi-strategy approach:
    // Strategy 1: Use existing ID from details if it matches the expected ID for this name
    // Strategy 2: Look up ID by question name (handles reordering)
    // Strategy 3: Use index-based matching for rename detection
    tempQuestions = tempQuestions.map((q, index) => {
      let id = null;
      
      // Strategy 1: Check if question already has an ID that belongs to it
      if (q.questionId) {
        const expectedIdForName = questionIdentityMap.get(`name:${q.name}`);
        // Only use if ID matches what we expect for this question name
        // Prevents copied questions from stealing another question's ID
        if (expectedIdForName === q.questionId) {
          id = q.questionId;
        }
      }
      
      // Strategy 2: Look up by name (handles question reordering)
      if (!id) {
        id = questionIdentityMap.get(`name:${q.name}`);
      }
      
      // Strategy 3: Index-based matching for rename detection
      // Only use if this appears to be a rename (not an insertion)
      if (!id) {
        const indexMatch = questionIdentityMap.get(`index:${index}`);
        if (indexMatch) {
          const oldQ = updatedSurvey.questions?.[index];
          // Check if old question at this index still exists elsewhere (would mean reorder, not rename)
          const oldNameExistsElsewhere = oldQ && tempQuestions.some(tq => tq.name === oldQ.name);
          
          // Only match if: same index, same type, and old name doesn't exist elsewhere
          if (oldQ && oldQ.type === q.type && !oldNameExistsElsewhere) {
            id = indexMatch.id;
          }
        }
      }
      
      // Duplicate prevention: If ID was already assigned this pass, generate new one
      if (id && assignedIdsThisPass.has(id)) {
        id = null;
      }
      
      const finalId = id || Math.random().toString(36).substr(2, 9);
      assignedIdsThisPass.add(finalId);

      // Return question with assigned IDs for question, rows, and columns
      return {
        ...q,
        questionId: finalId,
        _internalId: finalId,
        // Ensure rows and columns have unique IDs
        rows: q.rows ? ensureRowIds(q.rows) : q.rows,
        columns: q.columns ? ensureColumnIds(q.columns) : q.columns,
        choices: q.choices ? ensureColumnIds(q.choices) : q.choices
      };
    });

    // Update details with new IDs (questionId, rowId, columnId)
    surveyDetails.pages.forEach(page => {
      if (page.elements) {
        page.elements = page.elements.map(el => {
          // Match by name as it's the most reliable link back to the source in this context
          const matchedQ = tempQuestions.find(tq => tq.name === el.name); 
          if (matchedQ) {
            return { 
              ...el, 
              questionId: matchedQ.questionId,
              rows: matchedQ.rows,
              columns: matchedQ.columns,
              choices: matchedQ.choices
            };
          }
          return el;
        });
      }
    });
    updatedSurvey.details = JSON.stringify(surveyDetails);

    updatedSurvey.questions = tempQuestions;
    
    /**
     * UPDATE SURVEY QUESTION RESPONSE RULES
     * 
     * Rules link to questions via IDs. When questions/rows/columns are renamed,
     * we use the ID-based lookup to find the current data and update display values.
     * 
     * Rule matching priority:
     * 1. _questionInternalId / questionId - Most reliable, survives renames
     * 2. surveyQuestionName - Fallback for legacy rules without IDs
     * 
     * For rows (matrixSubQuestionRowId) and columns (conditionColumnId):
     * - First try ID-based lookup (survives renames)
     * - Fall back to value-based lookup (for legacy rules)
     * - If found, update the display values to current names
     * - If not found (deleted), clear the related fields
     */
    updatedSurvey.surveyQuestionResponseRules = updatedSurvey?.surveyQuestionResponseRules?.map(
      r => {
        // Find question by _questionInternalId/questionId first (most reliable), then by surveyQuestionName
        const lookupId = r._questionInternalId || r.questionId;
        let q = lookupId
          ? tempQuestions.find(q => q._internalId === lookupId || q.questionId === lookupId)
          : tempQuestions.find(q => q.name === r.surveyQuestionName);
        
        // --- ID-BASED ROW LOOKUP ---
        // Try to find row by matrixSubQuestionRowId first (handles renames), then by value
        let matchedRow = null;
        let matrixSubQuestionRowId = r.matrixSubQuestionRowId;
        if (q?.rows) {
          if (matrixSubQuestionRowId) {
            // Lookup by rowId (survives renames)
            matchedRow = q.rows.find(row => row.rowId === matrixSubQuestionRowId);
          }
          if (!matchedRow && r.matrixSubQuestionName) {
            // Fall back to lookup by value
            matchedRow = q.rows.find(row => {
              const rowValue = typeof row === 'string' ? row : (row.value || row.text);
              return rowValue === r.matrixSubQuestionName;
            });
            if (matchedRow) {
              matrixSubQuestionRowId = matchedRow.rowId;
            }
          }
        }
        // Get the current row value (may have been renamed)
        const currentRowValue = matchedRow ? (typeof matchedRow === 'string' ? matchedRow : (matchedRow.value || matchedRow.text)) : null;
        
        // --- ID-BASED COLUMN LOOKUP ---
        // Try to find column by conditionColumnId first (handles renames), then by value
        let matchedColumn = null;
        let conditionColumnId = r.conditionColumnId;
        const columnsOrChoices = q?.choices || q?.columns || [];
        if (conditionColumnId) {
          // Lookup by columnId (survives renames)
          matchedColumn = columnsOrChoices.find(col => col.columnId === conditionColumnId);
        }
        if (!matchedColumn && r.conditionData?.conditionValue) {
          // Fall back to lookup by value
          matchedColumn = columnsOrChoices.find(col => {
            const colValue = typeof col === 'string' ? col : (col.value || col.text);
            return colValue === r.conditionData.conditionValue;
          });
          if (matchedColumn) {
            conditionColumnId = matchedColumn.columnId;
          }
        }
        // Get the current column value and text (may have been renamed)
        const currentColumnValue = matchedColumn ? (typeof matchedColumn === 'string' ? matchedColumn : (matchedColumn.value || matchedColumn.text)) : null;
        const currentColumnText = matchedColumn ? (typeof matchedColumn === 'string' ? matchedColumn : (matchedColumn.text || matchedColumn.value)) : null;
        
        // Build the updated rule
        r = {
          ...r,
          _questionInternalId: q?._internalId,
          questionId: q?.questionId,
          type: q?.type,
          surveyQuestionName: q?.name || r.surveyQuestionName,
          matrixSubQuestionRowId: matrixSubQuestionRowId,
          conditionColumnId: conditionColumnId,
          choices: q?.choices || q?.columns || [],
          subQuestionOptions: q?.rows || [],
          conditionData: {
            ...r.conditionData,
            conditionValueText: currentColumnText || r.conditionData?.conditionValueText
          }
        };
        
        if(q) {
          // Question types that have columns/choices that need to be validated
          const QUESTION_TYPES_WITH_CHOICES = ['radiogroup', 'dropdown', 'checkbox', 'matrix', 'matrixdropdown','matrixdynamic'];
          const questionSupportsChoices = QUESTION_TYPES_WITH_CHOICES.includes(q.type);
          
          // Handle row removal/rename (only for matrix questions)
          if (r.matrixSubQuestionName !== '' && !matchedRow) {
            // Row was removed - clear the related data
            r = { 
              ...r,
              matrixSubQuestionName: '', 
              matrixSubQuestionRowId: '',
              conditionData: {
                condition: '',
                conditionValue: '',
                conditionValueText: ''
              },
              choices: columnsOrChoices,
              campaignUuid: '',
              isMatrix: true
            };
          } else if (matchedRow && currentRowValue !== r.matrixSubQuestionName) {
            // Row was renamed - update the value
            r = {
              ...r,
              matrixSubQuestionName: currentRowValue
            };
          }
          
          // Handle column removal/rename - ONLY for question types that have columns/choices
          // For text, comment, rating etc., conditionValue is user input, not a column reference
          if (questionSupportsChoices && (r.conditionData?.conditionValue !== '' || r.conditionData?.conditionValueText !== '') && !matchedColumn) {
            // Column was removed - clear the related data
            r = { 
              ...r,
              conditionColumnId: '',
              conditionData: {
                ...r.conditionData,
                conditionValue: '',
                conditionValueText: ''
              },
              campaignUuid: ''
            };
          } else if (matchedColumn) {
            // Column found - update values if renamed
            if (currentColumnValue !== r.conditionData?.conditionValue || currentColumnText !== r.conditionData?.conditionValueText) {
              r = {
                ...r,
                conditionData: {
                  ...r.conditionData,
                  conditionValue: currentColumnValue,
                  conditionValueText: currentColumnText
                }
              };
            }
          }

          // Always ensure choices array is set for questions that support choices (even if empty)
          // This preserves the question type intent when all options are removed
          if (questionSupportsChoices) {
            r.choices = columnsOrChoices; // Will be [] if all options removed, but type is preserved
          }

        } else {
          r = { 
            ...r,
            campaignUuid: '',
            surveyQuestionName: '',
            matrixSubQuestionName: '',
            matrixSubQuestionRowId: '',
            conditionColumnId: '',
            conditionData: {
              condition: '',
              conditionValue: '',
              conditionValueText: ''
            },
            choices: [],
            subQuestionOptions: [],
            isMatrix: false
          };
        }
        return r;
      }
    );

    /**
     * UPDATE PROMOTER SCORES (Post-Processing)
     * 
     * Promoter scores track questions by name in score.questions array.
     * When questions are renamed, we use questionIdMap to find the new names.
     * 
     * IMPORTANT: score.questions must remain a STRING ARRAY of question names
     * (not objects with IDs) for compatibility with ScoreBuilder.js component.
     * 
     * questionIdMap format: { oldQuestionName: questionId }
     * This allows us to trace: oldName -> questionId -> currentQuestion -> newName
     */
    // Create a deep copy of postProcessing to avoid mutating frozen objects
    // NOTE: score.questions must remain a STRING ARRAY (question names) for ScoreBuilder.js compatibility
    if (updatedSurvey.postProcessing?.promoterScores) {
      const updatedPromoterScores = updatedSurvey.postProcessing.promoterScores.map(score => {
        const updatedQuestionNames = (score.questions || []).map(qName => {
          // score.questions is a string array of question names
          
          // Check if this old name has a new name via questionIdMap
          // questionIdMap: { oldName: questionId }
          const questionId = questionIdMap[qName];
          
          if (questionId) {
            // Find the current question with this ID
            const currentQuestion = tempQuestions.find(tq => tq.questionId === questionId);
            if (currentQuestion) {
              return currentQuestion.name; // Return the new name
            }
          }
          
          // Fallback: check if question still exists by name
          const existingQuestion = tempQuestions.find(tq => tq.name === qName);
          if (existingQuestion) {
            return qName;
          }
          
          return qName; // Keep original (will be filtered out by ScoreBuilder if invalid)
        });
        
        return {
          ...score,
          questions: updatedQuestionNames
        };
      });
      
      // Create new postProcessing object instead of mutating
      updatedSurvey = {
        ...updatedSurvey,
        postProcessing: {
          ...updatedSurvey.postProcessing,
          promoterScores: updatedPromoterScores
        }
      };
    }

    setThisSurvey(updatedSurvey);
    } catch (error) {
      console.error('Error in handleDesignChange:', error);
      // Fallback to setting survey without modifications if error occurs
      setThisSurvey(tempSurvey);
    }
  };

  return {
    thisSurvey,
    thisSurveyResults,
    thisSurveyPreview,
    validationErrors,
    handleChange,
    updateEditorTheme,
    handleAutomationChange,
    handleLogoChange,
    handleSurveyPermissions,
    onTabChange,
    goNext,
    goBack,
    navBackOnError,
    saveSurvey,
    onFocus,
    onBlur,
    showFormSaveError,
    getErrorMessage,
    shouldShowError,
    updateCurrentErrorValidationSegment,
    supportedInternalSurveyResponseTypeOptions,
    currentContext,
    campaigns,
    isLoading,
    isSurveyLoading,
    isTemplateSaved,
    isGettingCampaign,
    thisIndividualSurveyResults,
    surveySettings,
    closeSurvey,
    setCurrentTemplateDetails,
    isTemplateReadOnly,
    onClickSendResponse,
    onClickCustomizeAndSend,
    updateTemplateModelProperty,
    handleSettingsChange,
    closeSurveySettings,
    setSurveyResultData,
    handleTypeChange,
    handleSubscriberListChange,
    handleDesignChange
  };
}
